import React, { useEffect, useRef } from 'react';
import { TextField, TextFieldProps, Box } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

/**
 * Generic React Hook Form text field component that properly maintains focus during renders
 */
interface HookFormTextFieldProps extends Omit<TextFieldProps, 'onChange' | 'size'> {
  name: string;
  fieldId: string; // Unique ID for this field within the form
  value: string;
  onChange: (value: string) => void;
  label: string;
  validationPattern?: RegExp;
  sizeVariant?: 'regular' | 'small'; // Renamed to avoid conflict with MUI's size prop
}

const HookFormTextField: React.FC<HookFormTextFieldProps> = ({ 
  name,
  fieldId,
  value,
  onChange,
  label,
  validationPattern,
  sizeVariant = 'regular', // Default to regular size
  ...props
}) => {
  // Use React Hook Form's controller for better focus management
  const { control, setValue } = useForm({
    defaultValues: {
      [fieldId]: value
    }
  });
  
  // Track whether focus is controlled by React Hook Form
  const hookFormActive = useRef(true);
  
  // Use effect to update form value when external state changes
  useEffect(() => {
    // Only update the form if the change is coming from outside
    if (!hookFormActive.current) {
      setValue(fieldId, value);
    }
  }, [value, fieldId, setValue]);
  
  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      my: 0.2,
      px: 0.5 // Add consistent horizontal padding
    }}>
      <Box sx={{ width: '100%' }}>
        <Controller
          name={fieldId}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              {...props}
              fullWidth
              label={label}
              autoComplete="off"
              size="small"
              sx={{ 
                '& .MuiInputBase-root': { 
                  margin: 0,
                  padding: 0,
                  height: '28px'
                },
                '& .MuiOutlinedInput-input': {
                  padding: '4px 14px',
                  height: '20px',
                  boxSizing: 'border-box'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.75rem',
                  transform: 'translate(14px, 8px) scale(1)'
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)'
                },
                '& .MuiOutlinedInput-notchedOutline': { 
                  transition: 'none' 
                },
                '&:hover .MuiOutlinedInput-notchedOutline': { 
                  transition: 'none' 
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                  transition: 'none' 
                },
                ...(props.sx || {})
              }}
              onChange={(e) => {
                const newValue = e.target.value;
                
                // Validate if pattern is provided
                if (validationPattern && newValue !== '' && !validationPattern.test(newValue)) {
                  return; // Don't update if it doesn't match pattern
                }
                
                // Mark that this change is from React Hook Form
                hookFormActive.current = true;
                
                // Update the field in React Hook Form
                field.onChange(e);
                
                // Pass the change to the parent component
                onChange(newValue);
                
                // Reset the flag after the update
                setTimeout(() => {
                  hookFormActive.current = false;
                }, 0);
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default React.memo(HookFormTextField);